import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { buildContactPath } from "@/lib/contactPath";

const heroBg = "/images/hero-sydney.webp";
const heroBg960 = "/images/hero-sydney-960.webp";
const heroBg1600 = "/images/hero-sydney-1600.webp";

const HeroSection = () => {
  return (
    <section className="relative h-[100dvh] min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          srcSet={`${heroBg960} 960w, ${heroBg1600} 1600w, ${heroBg} 1920w`}
          sizes="100vw"
          alt="Modern Sydney apartment with harbour views"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
        />
        {/* Dark, clean scrim to keep copy legible */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0.74) 0%, rgba(0,0,0,0.48) 45%, rgba(0,0,0,0.82) 100%)'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-14 sm:pb-16">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-[2.45rem] sm:text-5xl md:text-6xl lg:text-7xl text-white mb-6 sm:mb-8 leading-[1.02]"
            style={{ fontWeight: 300, letterSpacing: '-0.02em' }}
          >
            Your property earns more.
            <br />
            <span className="italic" style={{ fontWeight: 300 }}>We handle the rest.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-[1.05rem] sm:text-lg md:text-xl text-white mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed px-1"
            style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.4)' }}
          >
            We manage everything — guests, pricing, compliance, cleaning.
            You collect the payment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.48 }}
            className="mb-8 sm:mb-10 flex flex-wrap items-center justify-center gap-2.5"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-black/35 px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-white backdrop-blur-sm">
              <Star className="h-3.5 w-3.5 fill-current" />
              4.9 Rating
            </span>
            <span className="inline-flex items-center rounded-full border border-white/30 bg-black/35 px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-white backdrop-blur-sm">
              200+ owner and guest reviews
            </span>
          </motion.div>

          {/* Platform trust strip */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="text-white/55 text-xs sm:text-sm font-medium tracking-wider mb-6 sm:mb-8 uppercase"
          >
            Listed on Airbnb · Booking.com · Stayz
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4"
          >
            <Button asChild size="lg" className="w-full sm:w-auto rounded-full px-8 sm:px-12 py-6 sm:py-7 text-sm sm:text-base font-bold bg-white text-[#111827] hover:bg-white/90 shadow-2xl transition-all duration-300 hover:-translate-y-1 min-h-[48px]">
              <Link to="/calculator">
                Get your free estimate
                <ArrowRight className="ml-3 h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full sm:w-auto rounded-full px-8 sm:px-12 py-6 sm:py-7 text-sm sm:text-base font-bold border-2 border-white/60 text-white bg-transparent hover:bg-white/10 min-h-[48px]"
            >
              <Link to="/how-it-works">
                See how it works
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
